/**
 * Sporingshistorikk for en båt — utvidbar visning brukt både i Trackere og Båter.
 *
 * Viser ALLE sporpunkter for båten uansett kilde (telefon-sporing og maskinvare-
 * tracker), fordelt på regattaer. Klikk en regatta for å filtrere punktlista.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString('no-NO', {
    dateStyle: 'short',
    timeStyle: 'medium',
    timeZone: 'Europe/Oslo',
  });
}

export function TrackHistory({ source, id }: { source: 'tracker' | 'boat'; id: string }) {
  const [race, setRace] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['track-history', source, id, race],
    queryFn: () =>
      source === 'tracker'
        ? api.trackerPoints(id, 100, race ?? undefined)
        : api.boatPoints(id, 100, race ?? undefined),
    refetchInterval: 15_000,
  });

  if (q.isLoading) return <p className="text-xs text-slate-500 mt-2">Laster sporinger…</p>;
  const d = q.data;
  if (!d) return <p className="text-xs text-red-300 mt-2">Kunne ikke hente sporinger.</p>;
  if (!d.boatSail)
    return (
      <p className="text-xs text-slate-500 mt-2">
        Trackeren er ikke knyttet til en båt — ingen sporinger.
      </p>
    );

  return (
    <div className="mt-2 border-t border-slate-800 pt-2">
      <div className="text-xs text-slate-400 mb-2">
        Båt <span className="font-mono">{d.boatSail}</span> · {d.total} punkter totalt ·{' '}
        <span className={d.last24h > 0 ? 'text-emerald-400' : ''}>{d.last24h} siste døgn</span>
        <span className="text-slate-600"> · telefon og tracker</span>
      </div>

      {d.byRace.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            Historikk per regatta
          </div>
          <div className="rounded border border-slate-800 divide-y divide-slate-800/60 overflow-hidden">
            {d.byRace.map((r) => {
              const selected = race === r.raceSlug;
              return (
                <button
                  key={r.raceSlug}
                  type="button"
                  onClick={() => setRace(selected ? null : r.raceSlug)}
                  title="Klikk for å vise kun denne regattaen i lista under"
                  className={`flex items-center justify-between gap-2 px-2 py-1 text-xs w-full text-left transition ${
                    selected ? 'bg-sky-900/40 text-sky-200' : 'hover:bg-slate-800/60'
                  }`}
                >
                  <span className="truncate flex-1">
                    {selected ? '▸ ' : ''}
                    {r.raceName}
                  </span>
                  <span className="font-mono text-slate-300 shrink-0">{r.count} pkt</span>
                  <span className="text-slate-500 shrink-0 hidden sm:inline">
                    {fmtTime(r.first)} – {fmtTime(r.last)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {race && (
        <div className="flex items-center justify-between mb-1 text-xs">
          <span className="text-sky-300">
            Viser kun: {d.byRace.find((r) => r.raceSlug === race)?.raceName ?? race}
          </span>
          <button
            type="button"
            onClick={() => setRace(null)}
            className="text-slate-400 hover:text-slate-200"
          >
            Vis alle ✕
          </button>
        </div>
      )}

      {d.points.length === 0 ? (
        <p className="text-xs text-slate-500">
          Ingen sporpunkter registrert ennå. (Punkter lagres kun når båten er påmeldt en aktiv
          regatta.)
        </p>
      ) : (
        <div className="max-h-72 overflow-auto rounded border border-slate-800">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-2 py-1 font-medium">Tid</th>
                <th className="text-left px-2 py-1 font-medium">Posisjon</th>
                <th className="text-right px-2 py-1 font-medium">Fart</th>
                <th className="text-right px-2 py-1 font-medium">Kurs</th>
                <th className="text-left px-2 py-1 font-medium">Regatta</th>
              </tr>
            </thead>
            <tbody>
              {d.points.map((p, i) => (
                <tr key={i} className="border-t border-slate-800/60">
                  <td className="px-2 py-1 font-mono whitespace-nowrap">{fmtTime(p.recordedAt)}</td>
                  <td className="px-2 py-1 font-mono">
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${p.lat}&mlon=${p.lon}#map=15/${p.lat}/${p.lon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-400 hover:text-sky-300"
                    >
                      {p.lat.toFixed(4)}, {p.lon.toFixed(4)}
                    </a>
                  </td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    {p.sogKn != null ? `${p.sogKn.toFixed(1)} kn` : '—'}
                  </td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    {p.cog != null ? `${Math.round(p.cog)}°` : '—'}
                  </td>
                  <td className="px-2 py-1 truncate max-w-[10rem]">{p.raceName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
